/**
 * Simple toast notification service
 * Provides a clean way to display notifications without using alert()
 */

export interface ToastOptions {
    message: string
    type?: 'success' | 'error' | 'warning' | 'info'
    duration?: number
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

class ToastService {
    private container: HTMLElement | null = null

    constructor() {
        this.initContainer()
    }

    private initContainer() {
        // Create container if it doesn't exist
        if (!this.container) {
            this.container = document.createElement('div')
            this.container.className = 'toast-container'
            this.container.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                z-index: 10000;
                pointer-events: none;
            `
            document.body.appendChild(this.container)
        }
    }

    private createToast(options: ToastOptions): HTMLElement {
        const { message, type = 'info', duration = 4000 } = options

        const toast = document.createElement('div')
        toast.className = `toast toast-${type}`
        toast.style.cssText = `
            background: ${this.getBackgroundColor(type)};
            color: white;
            padding: 16px 24px;
            border-radius: 8px;
            margin-bottom: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 300px;
            max-width: 500px;
            pointer-events: auto;
            animation: slideIn 0.3s ease-out;
            font-size: 14px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        `

        // Add icon
        const icon = this.getIcon(type)
        toast.innerHTML = `
            <span style="font-size: 20px; flex-shrink: 0;">${icon}</span>
            <span style="flex: 1;">${message}</span>
            <button onclick="this.parentElement.remove()" style="
                background: transparent;
                border: none;
                color: white;
                font-size: 20px;
                cursor: pointer;
                padding: 0;
                line-height: 1;
                opacity: 0.7;
                transition: opacity 0.2s;
            " onmouseover="this.style.opacity='1'" onmouseout="this.style.opacity='0.7'">×</button>
        `

        // Auto remove after duration
        setTimeout(() => {
            toast.style.animation = 'slideOut 0.3s ease-in'
            setTimeout(() => toast.remove(), 300)
        }, duration)

        return toast
    }

    private getBackgroundColor(type: string): string {
        const colors = {
            success: '#10b981',
            error: '#ef4444',
            warning: '#f59e0b',
            info: '#3b82f6'
        }
        return colors[type as keyof typeof colors] || colors.info
    }

    private getIcon(type: string): string {
        const icons = {
            success: '✓',
            error: '✕',
            warning: '⚠',
            info: 'ℹ'
        }
        return icons[type as keyof typeof icons] || icons.info
    }

    show(options: ToastOptions | string) {
        if (!this.container) {
            this.initContainer()
        }

        const toastOptions: ToastOptions = typeof options === 'string'
            ? { message: options }
            : options

        const toast = this.createToast(toastOptions)
        this.container!.appendChild(toast)
    }

    success(message: string, duration?: number) {
        this.show({ message, type: 'success', duration })
    }

    error(message: string, duration?: number) {
        this.show({ message, type: 'error', duration })
    }

    warning(message: string, duration?: number) {
        this.show({ message, type: 'warning', duration })
    }

    info(message: string, duration?: number) {
        this.show({ message, type: 'info', duration })
    }
}

// Add CSS animations
const style = document.createElement('style')
style.textContent = `
    @keyframes slideIn {
        from {
            transform: translateX(100%);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
    
    @keyframes slideOut {
        from {
            transform: translateX(0);
            opacity: 1;
        }
        to {
            transform: translateX(100%);
            opacity: 0;
        }
    }
    
    @media (max-width: 768px) {
        .toast-container {
            left: 10px !important;
            right: 10px !important;
            top: 10px !important;
        }
        
        .toast {
            min-width: auto !important;
            max-width: 100% !important;
        }
    }
`
document.head.appendChild(style)

// Export singleton instance
export const toast = new ToastService()

// Make it available globally
declare global {
    interface Window {
        $toast: ToastService
    }
}

if (typeof window !== 'undefined') {
    window.$toast = toast
}

